import {
	useMutation as useVueMutation,
	type DefaultError,
	type MutationKey,
	type QueryClient,
	type UseMutationOptions,
	type UseMutationReturnType,
} from '@tanstack/vue-query';
import { ResultAsync } from 'neverthrow';

export type UseMutationReturn<TData, TError, TVariables, TOnMutateResult> =
	UseMutationReturnType<TData, TError, TVariables, TOnMutateResult> & {
		resultAsync: (
			...params: Parameters<UseMutationReturnType<TData, TError, TVariables, TOnMutateResult>['mutateAsync']>
		) => ResultAsync<TData, TError>;
	};

/**
 * `useMutation` with a **required** `mutationKey`, plus a `resultAsync` variant of
 * `mutateAsync` that returns a `ResultAsync` instead of throwing.
 *
 * The MutationCache tags Sentry errors with `mutationKey[0]` and labels network errors
 * with it, which have no endpoint to group by. Importing vue-query's `useMutation`
 * directly is banned via ESLint.
 */
export const useMutation = <
	TData = unknown,
	TError = DefaultError,
	TVariables = void,
	TOnMutateResult = unknown,
>(
	options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult> & {
		mutationKey: MutationKey;
	},
	queryClient?: QueryClient,
): UseMutationReturn<TData, TError, TVariables, TOnMutateResult> => {
	const mutation = useVueMutation(options, queryClient);

	return {
		...mutation,
		resultAsync: (...params) => ResultAsync.fromPromise(mutation.mutateAsync(...params), (error) => error as TError),
	};
};
